import React from "react";
import { motion } from "framer-motion";
import { ShieldCheck, Cpu, Clock, HardHat, ArrowRight, MessageSquare } from "lucide-react";
import LiveWorkflowDemo from "./LiveWorkflowDemo";

const easeOut = [0.33, 1, 0.68, 1];
const LINES = [
  { text: "STOP DOING", className: "text-gradient-white" },
  { text: "PAPERWORK,", className: "text-gradient-white" },
  { text: "LET DOX DO IT.", className: "text-gradient-brand" },
];

const BADGES = [
  { icon: HardHat, label: "Built for Contractors" },
  { icon: ShieldCheck, label: "Encrypted" },
  { icon: Cpu, label: "AI Powered" },
  { icon: Clock, label: "Works 24/7" },
];

function MotionLine({ children, index, className }) {
  return (
    <span className="reveal-mask">
      <motion.span
        className={`block ${className}`}
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1.1, delay: 0.15 + index * 0.14, ease: easeOut }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero({ onTrial, onTalkToDox }) {
  return (
    <section id="hero" className="relative overflow-hidden pt-8 pb-20 sm:pt-16 sm:pb-28 lg:pt-72">
      <div className="absolute inset-0 grid-lines opacity-60" />
      <div className="glow-orb w-[520px] h-[520px] -top-40 -left-32 bg-dox-purple/25" />
      <div className="glow-orb w-[460px] h-[460px] top-10 right-0 bg-dox-orange/20" />
      <div className="glow-orb w-[400px] h-[400px] top-64 left-1/3 bg-dox-magenta/15" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-b from-transparent to-ink" />

      <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col items-center text-center">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="glass inline-flex items-center gap-2 rounded-full px-4 py-1.5 mb-10"
          >
            <span className="h-2 w-2 rounded-full bg-dox-orange animate-pulseGlow" />
            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-white/70">
              Your AI Compliance Employee
            </span>
          </motion.div>

          <h1 className="font-display font-black tracking-[-0.03em] leading-[0.90] text-5xl sm:text-7xl lg:text-[8.5rem]">
            {LINES.map((line, i) => (
              <MotionLine key={i} index={i} className={line.className}>
                {line.text}
              </MotionLine>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7, duration: 0.8 }}
            className="mt-10 max-w-2xl text-base sm:text-lg text-white/60 leading-relaxed"
          >
            Upload your documents once. Dox reviews them, organizes them, monitors deadlines,
            prepares paperwork and keeps your business compliant — automatically.
          </motion.p>

          <motion.blockquote
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-6 text-white/45 italic text-sm sm:text-base"
          >
            &ldquo;I didn&apos;t buy software.&nbsp;<span className="text-white/70 not-italic font-semibold">I hired an AI employee.</span>&rdquo;
          </motion.blockquote>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.7 }}
            className="mt-10 flex flex-col sm:flex-row items-center gap-4"
          >
            <button
              data-testid="hero-trial-btn"
              onClick={onTrial}
              className="rounded-full bg-dox-orange px-8 py-4 min-h-[52px] text-base font-bold text-white shadow-[0_0_36px_rgba(30,120,255,0.5)] hover:bg-dox-orangeHover hover:scale-[1.03] active:scale-95 transition-all inline-flex items-center gap-2"
            >
              Start Your 14-Day Free Trial <ArrowRight size={18} />
            </button>
            <button
              data-testid="hero-talk-btn"
              onClick={onTalkToDox}
              className="rounded-full glass px-8 py-4 min-h-[52px] text-base font-semibold text-white hover:bg-white/10 transition-all inline-flex items-center gap-2"
            >
              <MessageSquare size={18} /> Talk to Dox
            </button>
          </motion.div>

          <div className="mt-14 flex flex-wrap items-center justify-center gap-x-8 gap-y-4">
            {BADGES.map((b) => (
              <div key={b.label} className="flex items-center gap-2 text-white/55">
                <b.icon size={16} className="text-dox-orange" />
                <span className="text-xs sm:text-sm font-medium tracking-wide">{b.label}</span>
              </div>
            ))}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 1, ease: easeOut }}
          className="relative mt-16 sm:mt-24"
        >
          <div className="absolute -inset-4 bg-gradient-to-r from-dox-orange/20 via-dox-magenta/20 to-dox-purple/20 blur-3xl rounded-[2rem]" />
          <div className="relative animate-float">
            <LiveWorkflowDemo />
          </div>
        </motion.div>
      </div>
    </section>
  );
}
